// components/Applicants/ApplicantsTable_NewUI.tsx
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";

import { usePaginatedApplicantsWithoutATS } from "../../../../../../data/applicants";
import {
  DataTable,
  IsSortedDataTableIcon,
} from "../../../../../../components/custom-ui/new-ui/DataTable";
import BillingInformationPagination from "../../Component/BillingInformation/BillingInformationPagination";
import DownloadIconWithLabel from "../../../../../dashboard/AdminMetricDashboard/DownLoadIcon";
// import GreenUnderlineHeading from "../../../../../../components/custom-ui/headings/heading";

const ApplicantsTable_NewUI = ({ jobId }: { jobId: string }) => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { applicants, resetAndFetch, loading } =
    usePaginatedApplicantsWithoutATS(jobId);

  useEffect(() => {
    if (jobId !== "") {
      resetAndFetch();
    }
  }, [jobId]);

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const filteredApplicants = useMemo(() => {
    if (!search.trim()) return applicants;
    const keyword = search.trim().toLowerCase();

    return applicants.filter((applicant: any) => {
      const name = applicant.applicantName?.toLowerCase() || "";
      const email = applicant.applicantEmail?.toLowerCase() || "";
      return name.includes(keyword) || email.includes(keyword);
    });
  }, [applicants, search]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentData = filteredApplicants.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredApplicants.length / rowsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleResultsPerPageChange = (e: any) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const exportToCSV = (data: any[]) => {
    if (!data || data.length === 0) return;

    const headers = ["Name", "Email", "Phone", "Applied On", "Resume Link"];
    const csvRows = [
      headers.join(","), // Header
      ...data.map((row) => {
        const applyDate =
          row.applyDate?.toDate?.() instanceof Date
            ? row.applyDate.toDate().toLocaleDateString()
            : new Date(row.applyDate).toLocaleDateString();

        return [
          `"${row.applicantName || ""}"`,
          `"${row.applicantEmail || ""}"`,
          `"${row.applicantPhone || ""}"`,
          `"${applyDate || ""}"`,
          `"${row.resumeLink || ""}"`,
        ].join(",");
      }),
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Applicants_List_${jobId}.csv`;;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: "applicantName",
        header: "Name",
      },
      {
        accessorKey: "applicantEmail",
        header: "Email",
      },
      {
        accessorKey: "applicantPhone",
        header: () => "Phone",
      },
      {
        accessorKey: "applyDate",
        header: () => "Applied On",
        cell: ({ row }: any) => (
          <div>
            {row.original.applyDate
              ? moment(
                  row.original.applyDate.toDate?.() || row.original.applyDate,
                ).format("DD MMM YYYY")
              : "—"}
          </div>
        ),
      },
      {
        accessorKey: "resumeLink",
        header: () => "Resume",
        cell: ({ row }: any) => (
          <div>
            {row.original.resumeLink ? (
              <a
                href={row.original.resumeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-600"
              >
                View Resume
              </a>
            ) : (
              "—"
            )}
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="w-full">
      {/* <div className="pb-5">
        <GreenUnderlineHeading title="Applicants List" />
      </div> */}
      <div className="pb-5 flex justify-end items-center gap-4">
        <div onClick={() => exportToCSV(applicants)} className="cursor-pointer">
          <DownloadIconWithLabel
            tooltipText="Download Applicants List"
            viewDown={true}
          />
        </div>

        <div className="relative w-full max-w-sm">
          <div className="pl-3 absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search Applicants"
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value);
              setSearch(value.trim());
              setCurrentPage(1);
            }}
            className="pl-10 pr-4 py-2 border rounded-md w-full"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={currentData || []}
        isloading={loading}
        isInactiveSelected={false}
      />

      <div className="flex-shrink-0">
        <BillingInformationPagination
          handleResultsPerPageChange={handleResultsPerPageChange}
          paginate={paginate}
          currentPage={currentPage}
          datatableDataPerPage={rowsPerPage}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
};

export default ApplicantsTable_NewUI;
